import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, Filter, Trophy, BookOpen, Star, Users, TrendingUp, Bookmark, Sparkles, GitFork, Award } from "lucide-react";

const SORT_OPTIONS = [
  { key: "trending", label: "Trending", testid: "sort-trending" },
  { key: "most_used", label: "Most used", testid: "sort-most-used" },
  { key: "best_outcome", label: "Best outcome", testid: "sort-best-outcome" },
  { key: "highest_rated", label: "Highest rated", testid: "sort-highest-rated" },
  { key: "new", label: "New", testid: "sort-new" },
];

const TABS = [
  { key: "browse", label: "Browse", icon: BookOpen, testid: "tab-browse" },
  { key: "leaderboard", label: "Top performers", icon: Trophy, testid: "tab-leaderboard" },
  { key: "featured", label: "Featured", icon: Award, testid: "tab-featured" },
  { key: "saved", label: "My saves", icon: Bookmark, testid: "tab-saved" },
  { key: "mine", label: "Mine", icon: Sparkles, testid: "tab-mine" },
];

export default function Community() {
  const [tab, setTab] = useState("browse");
  const [items, setItems] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("trending");
  const [q, setQ] = useState("");
  const [complaint, setComplaint] = useState("");
  const [muscle, setMuscle] = useState("");
  const [saves, setSaves] = useState([]);
  const [contribute, setContribute] = useState(true);

  const loadSettings = async () => {
    try {
      const r = await api.get("/community/settings");
      setContribute(r.data.community_contribute);
    } catch {
      // Settings are optional — the page works without them.
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "leaderboard") {
        const r = await api.get("/community/leaderboard?sort=improvement_pct&limit=25");
        setLeaders(r.data);
      } else if (tab === "saved") {
        const r = await api.get("/community/me/saves");
        setSaves(r.data);
      } else if (tab === "mine") {
        const r = await api.get("/community/protocols?mine=true&sort=new&limit=200");
        setItems(r.data);
      } else if (tab === "featured") {
        const r = await api.get("/community/protocols?featured=true&sort=featured&limit=100");
        setItems(r.data);
      } else {
        const params = new URLSearchParams();
        params.set("sort", sort);
        if (q.trim()) params.set("q", q.trim());
        if (complaint.trim()) params.set("complaint", complaint.trim());
        if (muscle.trim()) params.set("muscle", muscle.trim());
        const r = await api.get(`/community/protocols?${params.toString()}`);
        setItems(r.data);
      }
    } catch (e) {
      toast.error("Could not load community library.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, sort]);

  const toggleContribute = async () => {
    const next = !contribute;
    setContribute(next);
    try {
      await api.patch("/community/settings", { community_contribute: next });
      toast.success(next ? "Contribution enabled" : "Contribution disabled");
    } catch {
      toast.error("Could not update setting");
      setContribute(!next);
    }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 text-terracotta-600">Clinical outcomes network</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900">Community library</h1>
          <p className="text-ink-700 mt-3 max-w-2xl">
            Evidence-informed protocols, anonymized and shared by therapists who get results. Save, fork, rate,
            and watch what's working across the network.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 px-4 py-2 rounded-full border border-paper-200 bg-white text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={contribute}
              onChange={toggleContribute}
              className="w-4 h-4 rounded border-paper-200 text-sage-500 focus:ring-sage-500/30"
              data-testid="community-contribute-toggle"
            />
            <span className="text-ink-900">Contribute anonymized data</span>
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="community-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={t.testid}
              className={`min-h-[40px] px-4 rounded-full text-sm inline-flex items-center gap-2 transition-all ${
                active ? "bg-ink-900 text-white" : "text-ink-700 hover:bg-paper-100 border border-paper-200"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "browse" && (
        <div className="bg-white border border-paper-200 rounded-3xl p-5 mb-6">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="flex-1 min-w-[220px] flex items-center gap-2 px-4 py-2 rounded-full bg-paper-50 border border-paper-200">
              <Search className="w-4 h-4 text-ink-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder='Search by name, description or condition (e.g., "plantar fasciitis")'
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="community-search-input"
              />
            </div>
            <input
              value={complaint}
              onChange={(e) => setComplaint(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Complaint tag (e.g., sciatica)"
              className="min-h-[40px] px-4 rounded-full bg-paper-50 border border-paper-200 text-sm outline-none"
              data-testid="community-complaint-input"
            />
            <input
              value={muscle}
              onChange={(e) => setMuscle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Muscle id (e.g., upper-trap-left)"
              className="min-h-[40px] px-4 rounded-full bg-paper-50 border border-paper-200 text-sm outline-none"
              data-testid="community-muscle-input"
            />
            <button
              onClick={load}
              className="min-h-[40px] px-5 rounded-full bg-sage-500 text-white text-sm font-medium hover:bg-sage-600 inline-flex items-center gap-2"
              data-testid="community-search-btn"
            >
              <Filter className="w-4 h-4" /> Apply
            </button>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {SORT_OPTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSort(s.key)}
                data-testid={s.testid}
                className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                  sort === s.key ? "bg-ink-900 text-white" : "text-ink-700 hover:bg-paper-100 border border-paper-200"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="text-ink-500" data-testid="community-loading">Loading…</div>}

      {!loading && tab === "leaderboard" && (
        <Leaderboard rows={leaders} />
      )}

      {!loading && tab === "saved" && (
        <SavedList saves={saves} />
      )}

      {!loading && (tab === "browse" || tab === "mine" || tab === "featured") && (
        <ProtocolGrid items={items} emptyText={
          tab === "mine" ? "You haven't published any protocols yet. Go to Protocols → tap Share." :
          tab === "featured" ? "No featured protocols yet." :
          "No protocols match your filters."
        } />
      )}
    </Layout>
  );
}

function ProtocolGrid({ items, emptyText }) {
  if (!items?.length) {
    return (
      <div className="bg-white border border-paper-200 rounded-3xl p-12 text-center" data-testid="community-empty">
        <div className="font-heading text-xl text-ink-900 mb-2">Nothing here yet</div>
        <p className="text-ink-700 max-w-md mx-auto">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-grid">
      {items.map((p) => <ProtocolCard key={p.id} p={p} />)}
    </div>
  );
}

function ProtocolCard({ p }) {
  return (
    <Link
      to={`/community/protocols/${p.id}`}
      data-testid={`community-card-${p.id}`}
      className="bg-white border border-paper-200 rounded-2xl p-5 hover:border-sage-500/40 hover:shadow-md transition-all flex flex-col"
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="font-heading text-lg font-medium text-ink-900 flex-1 min-w-0">{p.name}</div>
        {p.is_featured && (
          <span className="shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200" data-testid="featured-badge">
            Featured
          </span>
        )}
      </div>
      <div className="text-xs text-ink-500 mb-2 flex items-center gap-2">
        <Users className="w-3 h-3" />
        <span>{p.author_display_name}</span>
        {p.lineage_depth > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-700"><GitFork className="w-3 h-3" /> fork</span>
        )}
      </div>
      {p.description && <p className="text-sm text-ink-700 mb-3 line-clamp-2">{p.description}</p>}
      {p.complaint_tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {p.complaint_tags.slice(0, 4).map((t) => (
            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-terracotta-50 text-terracotta-700 border border-terracotta-100">{t}</span>
          ))}
        </div>
      )}
      <div className="mt-auto grid grid-cols-4 gap-2 text-xs pt-3 border-t border-paper-200">
        <Stat icon={<Bookmark className="w-3 h-3" />} value={p.saves_count || 0} label="saves" />
        <Stat icon={<TrendingUp className="w-3 h-3" />} value={p.uses_count || 0} label="uses" />
        <Stat icon={<Star className="w-3 h-3" />} value={p.avg_rating != null ? p.avg_rating.toFixed(1) : "—"} label={`${p.ratings_count || 0} rate`} />
        <Stat icon={<Sparkles className="w-3 h-3" />} value={p.improvement_pct != null ? `${p.improvement_pct}%` : "—"} label="improved" />
      </div>
    </Link>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-0.5 text-ink-700">
      <div className="flex items-center gap-1 font-heading text-sm text-ink-900">{icon}{value}</div>
      <div className="text-[10px] text-ink-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function Leaderboard({ rows }) {
  if (!rows?.length) {
    return (
      <div className="bg-white border border-paper-200 rounded-3xl p-12 text-center" data-testid="leaderboard-empty">
        <Trophy className="w-10 h-10 mx-auto text-ink-300 mb-3" />
        <div className="font-heading text-xl text-ink-900 mb-2">No leaderboard data yet</div>
        <p className="text-ink-700 max-w-md mx-auto">Therapists rise on the leaderboard once their protocols have been applied to at least 3 sessions with outcome data.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-paper-200 rounded-3xl overflow-hidden" data-testid="leaderboard">
      <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-paper-50 border-b border-paper-200 text-[10px] uppercase tracking-wider text-ink-500">
        <div className="col-span-1">#</div>
        <div className="col-span-4">Therapist</div>
        <div className="col-span-2 text-right">Improvement</div>
        <div className="col-span-2 text-right">Avg Δ pain</div>
        <div className="col-span-1 text-right">Uses</div>
        <div className="col-span-2 text-right">Protocols</div>
      </div>
      {rows.map((r, i) => (
        <div key={r.author_display_name} data-testid={`leader-row-${i}`} className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-paper-100 hover:bg-paper-50 transition-colors text-sm">
          <div className="col-span-1 font-heading text-ink-900">{i + 1}</div>
          <div className="col-span-4 flex items-center gap-2 text-ink-900">
            {i === 0 && <Trophy className="w-4 h-4 text-amber-500" />}
            {i === 1 && <Trophy className="w-4 h-4 text-ink-400" />}
            {i === 2 && <Trophy className="w-4 h-4 text-terracotta-400" />}
            <span>{r.author_display_name}</span>
          </div>
          <div className="col-span-2 text-right text-sage-700 font-medium">{r.improvement_pct || 0}%</div>
          <div className="col-span-2 text-right text-ink-700">{r.avg_vas_delta ? r.avg_vas_delta.toFixed(2) : "—"}</div>
          <div className="col-span-1 text-right text-ink-700">{r.uses_count || 0}</div>
          <div className="col-span-2 text-right text-ink-700">{r.protocols_count || 0}</div>
        </div>
      ))}
    </div>
  );
}

function SavedList({ saves }) {
  if (!saves?.length) {
    return (
      <div className="bg-white border border-paper-200 rounded-3xl p-12 text-center" data-testid="saved-empty">
        <Bookmark className="w-10 h-10 mx-auto text-ink-300 mb-3" />
        <div className="font-heading text-xl text-ink-900 mb-2">No saved protocols yet</div>
        <p className="text-ink-700 max-w-md mx-auto">Browse the community library and tap "Save to my protocols" — saved protocols appear here and in your private Protocols library.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {saves.map((s) => <ProtocolCard key={s.protocol.id} p={s.protocol} />)}
    </div>
  );
}
